/**
 * The idempotency key a queued event carries — offline-sync.md §6.
 *
 * 🔴 MINTED WHEN THE EVENT IS QUEUED, NOT WHEN IT IS SENT. The key exists so
 * that "the request landed but the response was lost" is recognisable as the
 * same event when the outbox sends it again. A key minted per send would be a
 * new key per retry, which is the duplicate row this exists to prevent.
 *
 * ⚠️ A v4 UUID, BECAUSE THE SERVER REFUSES ANYTHING ELSE — and a refusal is a
 * 400, which the outbox discards. Every branch below produces the same
 * version and variant bits for that reason.
 */

/** The two halves of Web Crypto this needs, so a test can hand it either. */
export type EventIdCrypto = Partial<Pick<Crypto, 'randomUUID' | 'getRandomValues'>>

export function mintEventId(
  cryptoApi: EventIdCrypto | undefined = globalThis.crypto as EventIdCrypto | undefined,
): string {
  /*
   * `randomUUID` only exists in a SECURE CONTEXT (HTTPS or localhost) and on
   * browsers from 2021–22 onwards. A school that opens the app on a LAN address
   * over plain HTTP, or a laptop on an old Safari, has `getRandomValues` and
   * not this — so it is the first choice, not the only one.
   */
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID()

  const bytes = new Uint8Array(16)
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes)
  } else {
    /*
     * ⚠️ `Math.random` ONLY WHERE THERE IS NO WEB CRYPTO AT ALL, and it is safe
     * here for a reason specific to this key. Its weakness is predictability,
     * and predicting this id buys nothing: the server scopes it to the player
     * the device token names (`@@unique([playerId, clientEventId])`), so a
     * guessed id can only ever collide with that child's own events. All it has
     * to be is unique among one child's events, which 122 bits from any
     * reasonable generator are.
     */
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }

  // RFC 9562 §5.4: version 4 in the high nibble of byte 6, variant 10xx in byte 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
